// src/components/tutorials/JS_Function_Declaration.jsx
import React, { useRef } from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";
import TryItButton from "../../../TryItButton";
import OutputBox from "../../../OutputBox";

export default function JS_Function() {
  const refs = {
    basic: useRef(null),
    hoisting: useRef(null),
    parameters: useRef(null),
    scope: useRef(null),
    returnValue: useRef(null),
  };

  const scrollTo = (key) =>
    refs[key]?.current?.scrollIntoView({ behavior: "smooth", block: "start" });

  const keyPoints = [
    { key: "basic", label: "Basic Function Declaration" },
    { key: "hoisting", label: "Function Hoisting" },
    { key: "parameters", label: "Function Parameters" },
    { key: "scope", label: "Scope and Accessibility" },
    { key: "returnValue", label: "Return Values" },
  ];

  return (
    <div className="tutorial-container">
      <h1 className="tutorial-title">🧩 JavaScript Function Declarations</h1>
      <p className="tutorial-intro">
        A <strong>function declaration</strong> defines a named function using
        the <code className="highlight_code">function</code> keyword. These are
        hoisted, reusable, and form the foundation of JavaScript programming.
      </p>

      {/* 🔑 Key Points */}
      <div
        style={{
          background: "linear-gradient(135deg, #fef3c7, #fde68a)",
          padding: "1.5rem 2.5rem",
          borderRadius: "12px",
          marginTop: "1rem",
          marginBottom: "3rem",
        }}
      >
        <h2 style={{ color: "#92400e", marginBottom: "1rem" }}>
          🔑 Key Points
        </h2>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "0.5rem",
          }}
        >
          {keyPoints.map((item) => (
            <p key={item.key} style={{ margin: "0.25rem 0" }}>
              <button
                onClick={() => scrollTo(item.key)}
                style={{
                  background: "transparent",
                  border: "none",
                  textDecoration: "underline",
                  color: "black",
                  cursor: "pointer",
                  fontSize: "1.05rem",
                }}
              >
                {item.label}
              </button>
            </p>
          ))}
        </div>
      </div>

      {/* 1️⃣ Basic Function Declaration */}
      <div ref={refs.basic} className="tutorial-section">
        <h3>1️⃣ Basic Function Declaration</h3>
        <p>
          Declared using the <code className="highlight_code">function</code>{" "}
          keyword followed by a name, parameters, and body.
        </p>
        <div className="code-demo-box read-only-box">
          <CodeCopyBlock
            code={`function greet(name) {
  console.log("Hello, " + name + "!");
}

greet("Alice");`}
          />
          <TryItButton
            code={`function greet(name){console.log("Hello, "+name+"!");}greet("Alice");`}
          />
        </div>
        <OutputBox>Output: Hello, Alice!</OutputBox>
      </div>

      {/* 2️⃣ Function Hoisting */}
      <div ref={refs.hoisting} className="tutorial-section">
        <h3>2️⃣ Function Hoisting</h3>
        <p>
          Function declarations are <strong>hoisted</strong> to the top of their
          scope. You can call them before their definition appears in the code.
        </p>
        <div className="code-demo-box read-only-box">
          <CodeCopyBlock
            code={`sayHi(); // Works even before declaration

function sayHi() {
  console.log("Hi there!");
}`}
          />
          <TryItButton
            code={`sayHi();function sayHi(){console.log("Hi there!");}`}
          />
        </div>
        <OutputBox>Output: Hi there!</OutputBox>
      </div>

      {/* 3️⃣ Function Parameters */}
      <div ref={refs.parameters} className="tutorial-section">
        <h3>3️⃣ Function Parameters</h3>
        <p>
          You can pass arguments to functions using parameters. Default values
          can also be provided.
        </p>
        <div className="code-demo-box read-only-box">
          <CodeCopyBlock
            code={`function add(a, b = 0) {
  return a + b;
}

console.log(add(5, 3)); // 8
console.log(add(5));   // 5`}
          />
          <TryItButton
            code={`function add(a,b=0){return a+b;}console.log(add(5,3));console.log(add(5));`}
          />
        </div>
        <OutputBox>
          Output:
          <pre>
            8
            <br />5
          </pre>
        </OutputBox>
      </div>

      {/* 4️⃣ Scope and Accessibility */}
      <div ref={refs.scope} className="tutorial-section">
        <h3>4️⃣ Scope and Accessibility</h3>
        <p>
          Functions declared in the global scope can be accessed anywhere. Those
          declared inside another function are local to that function.
        </p>
        <div className="code-demo-box read-only-box">
          <CodeCopyBlock
            code={`function outer() {
  function inner() {
    console.log("Inner function");
  }
  inner(); // Works
}

outer();
// inner(); // ❌ Error: inner is not defined`}
          />
          <TryItButton
            code={`function outer(){function inner(){console.log("Inner function");}inner();}outer();try{inner();}catch(e){console.log("Error:",e.message);}`}
          />
        </div>
        <OutputBox>
          Output:
          <pre>
            Inner function
            <br />
            Error: inner is not defined
          </pre>
        </OutputBox>
      </div>

      {/* 5️⃣ Return Values */}
      <div ref={refs.returnValue} className="tutorial-section">
        <h3>5️⃣ Return Values</h3>
        <p>
          A function can return a value using the{" "}
          <code className="highlight_code">return</code> keyword. If omitted,
          the function returns <code>undefined</code>.
        </p>
        <div className="code-demo-box read-only-box">
          <CodeCopyBlock
            code={`function multiply(a, b) {
  return a * b;
}

let result = multiply(4, 5);
console.log(result); // 20`}
          />
          <TryItButton
            code={`function multiply(a,b){return a*b;}let result=multiply(4,5);console.log(result);`}
          />
        </div>
        <OutputBox>Output: 20</OutputBox>
      </div>

      {/* 🚀 Next Steps */}
      <div
        style={{
          background: "linear-gradient(135deg, #fef9c3, #fde68a)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginTop: "2rem",
        }}
      >
        <h2 style={{ color: "#92400e" }}>🚀 Next Steps</h2>
        <ul className="tutorial-list-points">
          <li>
            Compare declarations with function expressions and arrow functions.
          </li>
          <li>Understand how hoisting works differently in each type.</li>
          <li>Practice writing modular, reusable functions.</li>
        </ul>
      </div>
    </div>
  );
}
